import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import Request from "common/Request";
import help from "help/viewDPIServiceMetrics.md";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import {
  getPolicyFilterCmd,
  namesReported,
  parseApplicationMetrics,
  parsePolicyNames,
  parseSubscriberGroupsNames,
} from "./api";
import SelectMultiAutocomplete from "common/SelectMultiAutocomplete";
import { atBqnTarget, safeStr } from "common/api";
import Switch from "@mui/material/Switch";
import View from "common/View";
import BqnContext from "common/BqnContext";
import TimeChart from "./TimeChart";
import { AppChart } from "./utils";
import { hoursScopeChoices, periodAsHours } from "common/constants";
import useHoursOfReturnView from "common/hooks/useHoursOfReturnView";

const LayoutRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 750px) {
    flex-direction: column;
    align-items: flex-end;
    flex-wrap: wrap;

    .displaySelect {
      margin-right: 30px;
    }
  }
`;

const FiltersRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  @media (max-width: 800px) {
    flex-wrap: wrap;
    margin-top: 0px;
  }
`;

const FiltersLeft = styled.div`
  display: flex;
  & > div {
    margin-right: 10px;
  }

  @media (max-width: 800px) {
    width: 100%;
  }
`;

const SwitchWithLabel = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const Label = styled.div`
  margin-right: 10px;
  font-family: AllianceNo2-SemiBold !important;
  font-weight: 100;
  color: var(--Text-Primary);
  width: 100px;
  text-align: right;
`;

const MultiSelectorsWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  & > div {
    width: 50%;
    margin: 0 10px;
  }
  @media (max-width: 800px) {
    flex-direction: column;
    & > div {
      width: 100%;
      margin-right: 0px;
    }
  }
`;

const SelectWrapper = styled.div`
  width: 250px;
`;

const SwitchWrapper = styled.div`
  width: auto;
  display: flex;
  align-items: center;
  position: relative;
`;

const _displayChoices = [
  { value: "updown", label: "Downlink & Uplink Speed per Flow" },
  { value: "downall", label: "Downlink Speed & Time Evolution" },
  { value: "upall", label: "Uplink Speed & Time Evolution" },
];

const titles = {
  downlink: "Downlink speed per flow",
  uplink: "Uplink speed per flow",
};

const AppCharts = ({
  data,
  plotMode,
  display,
  bqn,
  hours,
  subscriberGroups,
  policiesRate,
  showMinMax,
  selectedApp,
}) => {
  const { downlink, uplink } = data;
  const [filteredAppsDown, setFilteredAppsDown] = useState(
    new Set()
  );
  const [filteredAppsUp, setFilteredAppsUp] = useState(new Set());

  useEffect(() => {
    const appsDownlink = data.downlink.map((item) => item.app);
    setFilteredAppsDown(new Set(appsDownlink));
  }, [data.downlink]);

  useEffect(() => {
    const appsUplink = data.uplink.map((item) => item.app);
    setFilteredAppsUp(new Set(appsUplink));
  }, [data.uplink]);
  return (
    <div className="margin-t-20" key={bqn}>
      <AppChart
        title={
          display === "downall" || display === "updown"
            ? titles.downlink
            : titles.uplink
        }
        data={display === "downall" || display === "updown" ? downlink : uplink}
        plotMode={plotMode}
        selection={
          display === "downall" || display === "updown"
            ? filteredAppsDown
            : filteredAppsUp
        }
        setSelection={
          display === "downall" || display === "updown"
            ? setFilteredAppsDown
            : setFilteredAppsUp
        }
        showMinMax={showMinMax}
      />
      {display === "updown" ? (
        <AppChart
          title={titles.uplink}
          data={data.uplink}
          plotMode={plotMode}
          selection={filteredAppsUp}
          setSelection={setFilteredAppsUp}
          showMinMax={showMinMax}
        />
      ) : null}
      {display === "downall" || display === "upall" ? (
        <TimeChart
          display={display}
          data={display === "downall" ? downlink : uplink}
          hours={hours}
          subscriberGroups={subscriberGroups}
          policiesRate={policiesRate}
          bqn={bqn}
          plotMode={plotMode}
          showMinMax={showMinMax}
          selectedAppInit={selectedApp}
        />
      ) : null}
    </div>
  );
};

const AppChartsBqns = ({
  data,
  plotMode,
  display,
  hours,
  subscriberGroups,
  policiesRate,
  showMinMax,
  selectedApp,
}) => {
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const entries = Object.entries(data);

  if (entries.length === 0) {
    return <h5 className="text-center margin-t-30">No data available</h5>;
  }

  const relevantBqns = mode === "cluster" && bqnId === 'all'
    ? () => true
    : ([bqn]) => parseFloat(bqn) === parseFloat(bqnId)
  const filteredEntries = entries.filter(relevantBqns);

  return <>
    {filteredEntries.map(([bqn, value]) =>
      <AppCharts
        key={bqn}
        data={value}
        plotMode={plotMode}
        display={display}
        bqn={bqn}
        hours={hours}
        subscriberGroups={subscriberGroups}
        policiesRate={policiesRate}
        showMinMax={showMinMax}
        mode={mode}
        selectedApp={selectedApp}
      />
    )}
  </>
};

function getSubscriberGroupFilterCmd(subscriberGroupSelected) {
  if (subscriberGroupSelected.length === 0) {
    return "all";
  } else {
    return `name '${subscriberGroupSelected
      .map((sub) => `"${safeStr(sub).replaceAll("'", "")}"`)
      .join(",")}'`;
  }
}

const ApplicationMetrics = ({ returnView = null, selectedApp }) => {
  const hoursOfReturnView = useHoursOfReturnView(returnView);
  const [hours, setHours] = useState(hoursOfReturnView ?? 24);
  const [request, setRequest] = useState(null);
  const [policiesRate, setPoliciesRate] = useState([]);
  const [policiesRateSelected, setPoliciesRateSelected] = useState([]);
  const [subscriberGroups, setSubscriberGroups] = useState([]);
  const [subscriberGroupSelected, setSubscriberGroupSelected] = useState([]);
  const [plotMode, setPlotMode] = useState("boxplot");
  const [display, setDisplay] = useState("downall");
  const [showMinMax, setShowMinMax] = useState(false);

  const actions = useContext(ActionsContext);

  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  // The BQN the filters loaded are of, and the mode they were asked in.
  const filtersTarget = `${mode}-${bqnId}`;
  const [filtersTargetLoaded, setFiltersTargetLoaded] = useState();

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The metrics wait for the filters of that BQN
  // too: a filter of the BQN before may name a policy or a subscriber group
  // this one does not report, and asking by it leaves the metrics empty.
  useEffect(() => {
    if (bqnId === undefined || filtersTargetLoaded !== filtersTarget) {
      return;
    }

    doLoad();

    return actions.recv("do-load", doLoad);
  }, [
    hours,
    policiesRateSelected.join(","),
    subscriberGroupSelected.join(","),
    mode,
    bqnId,
    filtersTargetLoaded,
  ]);

  // The filters of the BQN before are kept only as far as the BQN selected
  // now reports them. The answers of a BQN that is not the one selected any
  // more are dropped, so that the metrics are not asked for its filters.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    let isOfBqnSelected = true;

    doLoadFilters(() => isOfBqnSelected);

    return () => {
      isOfBqnSelected = false;
    };
  }, [mode, bqnId]);

  function doLoadFilters(isOfBqnSelected) {
    Promise.all([
      ifCl.run(atBqnTarget("show policy rate", mode, bqnId)),
      ifCl.run(atBqnTarget("show api subscriber-group", mode, bqnId)),
    ]).then(([policiesResp, subscriberGroupsResp]) => {
      if (!isOfBqnSelected()) {
        return;
      }

      const policies = parsePolicyNames(policiesResp);
      const groups = parseSubscriberGroupsNames(subscriberGroupsResp);

      setPoliciesRate(policies);
      setSubscriberGroups(groups);
      setPoliciesRateSelected(namesReported(policies));
      setSubscriberGroupSelected(namesReported(groups));
      setFiltersTargetLoaded(filtersTarget);
    });
  }

  function doLoad() {
    const request = ifCl.run(
            atBqnTarget(
              `show pkteng app-metrics hours ${hours} categories 19 subscriber all policy ${getPolicyFilterCmd(
                policiesRateSelected
              )} subscriber-group ${getSubscriberGroupFilterCmd(
                subscriberGroupSelected
              )} percentile`,
              mode,
              bqnId
            ),
            { timeout: DPI_STATS_TMOUT_MS }
          )
      .then((response) => parseApplicationMetrics(response, mode, bqnId));

    setRequest(request);
  }

  return (
    <View
      title="Application Speed Metrics"
      returnView={returnView}
      mdHelp={help}
    >
            <LayoutRow>
              <SelectWrapper className="displaySelect">
                <SelectInputWithIcon
                  title="Display"
                  name="Display"
                  icon="apps"
                  selected={display}
                  onChange={({ target }) => setDisplay(target.value)}
                  options={_displayChoices}
                />
              </SelectWrapper>
              <SwitchWrapper>
                <SwitchWithLabel>
                  <Label>Line chart view</Label>{" "}
                  <Switch
                    className="sw-input"
                    checked={plotMode !== "boxplot"}
                    onChange={() =>
                      setPlotMode(plotMode === "boxplot" ? "line" : "boxplot")
                    }
                  />
                </SwitchWithLabel>
                <SwitchWithLabel>
                  <Label>View max+min</Label>{" "}
                  <Switch
                    className="sw-input"
                    checked={showMinMax}
                    onChange={() => setShowMinMax(!showMinMax)}
                  />
                </SwitchWithLabel>
              </SwitchWrapper>
            </LayoutRow>
            <FiltersRow>
              <FiltersLeft>
                <SelectInputWithIcon
                  title="Date Range"
                  name="hours"
                  icon="date_range"
                  selected={hours || 24}
                  onChange={({ target }) => setHours(parseInt(target.value))}
                  options={hoursScopeChoices}
                />
              </FiltersLeft>
              <MultiSelectorsWrapper>
                <SelectMultiAutocomplete
                  icon="folder_open"
                  title="Subscriber Group"
                  name="subscriber-group-selector"
                  placeholder="Subscriber Group"
                  options={subscriberGroups.map((name) => {
                    return { label: name, value: name };
                  })}
                  key="subscriberGroupSelector"
                  selected={subscriberGroupSelected}
                  onChange={(value) =>
                    setSubscriberGroupSelected(
                      value.map((optionType) => optionType.value)
                    )
                  }
                />
                <SelectMultiAutocomplete
                  icon="folder_open"
                  title="Rate policy"
                  name="policy-rate-selector"
                  placeholder="Rate policy"
                  options={policiesRate.map((name) => {
                    return { label: name, value: name };
                  })}
                  key="policyRateSelector"
                  selected={policiesRateSelected}
                  onChange={(value) =>
                    setPoliciesRateSelected(
                      value.map((optionType) => optionType.value)
                    )
                  }
                />
              </MultiSelectorsWrapper>
            </FiltersRow>

            <Request during={request}>
              {(result) => (
                <AppChartsBqns
                  data={result}
                  plotMode={plotMode}
                  display={display}
                  hours={hours}
                  subscriberGroups={subscriberGroupSelected}
                  policiesRate={policiesRateSelected}
                  showMinMax={showMinMax}
                  selectedApp={selectedApp}
                />
              )}
            </Request>
    </View>
  );
};

export default ApplicationMetrics;
